import { Icon } from "@iconify/react/dist/iconify.js";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import io from "socket.io-client";
import { useDispatch, useSelector } from "react-redux";
import WebRTCListenerClient from "../utils/webrtc/listenerClient";
import { ClipLoader } from "react-spinners";
import {
  fetchEventImage,
  fetchSpecificEvent,
  addReaction,
  removeReaction,
  fetchComments,
  clearComments,
  fetchReactionCounts,
  receiveComment,
  receiveReactionAdd,
  receiveReactionRemove,
} from "../redux/slices/userSlice";

const SIGNALING_URL = "wss://telecoms.pluglr.com";
const MAX_CONNECTION_RETRIES = 10;
const RETRY_DELAY_MS = 2000;

const NonUserShell = styled.div`
  min-height: 100vh;
  background: #000000;
  color: #eff0fa;
  padding: 32px 16px 64px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;

  .all-the-audios {
    width: 100%;
    max-width: 1120px;
    display: flex;
    flex-direction: column;
    gap: 32px;
  }

  .control-button.secondary {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.12);
    color: #eff0fa;
  }

  .control-button.secondary.active {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.2);
  }

  .upper-audio {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
  }

  .identity-row,
  .status-group {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
  }

  .identity-row {
    flex: 1 1 auto;
    min-width: 240px;
    gap: 20px;
  }

  .status-group {
    flex: 0 1 auto;
    justify-content: flex-end;
  }

  .brand-mark {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: #1b1b1b;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
    border: 1px solid #272a31;
  }

  .title-block {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 8px 14px;
    border-radius: 999px;
    background: #242424;
  }

  .title-block p {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #e9e9ea;
    letter-spacing: 0.01em;
  }

  .upper-right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .audio-upper-middle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: 1px solid #272a31;
    border-radius: 999px;
    padding: 10px 16px;
    background: #1b1b1b;
  }

  .red-rounded {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #828286;
    transition: background 0.2s ease, box-shadow 0.2s ease;
  }

  .red-rounded.on {
    background: #ed1c24;
    box-shadow: 0 0 12px rgba(237, 28, 36, 0.65);
  }

  .audio-upper-middle p {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .audio-upper-middle span {
    color: #8f9099;
    font-size: 12px;
  }

  .status-chip {
    padding: 8px 16px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    background: rgba(59, 130, 246, 0.12);
    color: #93c5fd;
  }

  .status-chip.success {
    background: rgba(34, 197, 94, 0.18);
    color: #4ade80;
  }

  .status-chip.error {
    background: rgba(248, 113, 113, 0.18);
    color: #fca5a5;
  }

  .error-text {
    font-size: 12px;
    color: #f87171;
    margin: 0;
  }

  .call-status {
    font-size: 12px;
    color: #93c5fd;
    margin: 0;
  }

  .middle-image-box {
    background: #1b1b1b;
    border-radius: 20px;
    padding: 32px;
    display: flex;
    gap: 32px;
    align-items: stretch;
    position: relative;
    overflow: hidden;
    transition: flex-direction 0.25s ease, padding 0.25s ease;
  }

  .middle-image-box.with-controls {
    flex-direction: row;
  }

  .middle-image-box.expanded {
    flex-direction: column;
    padding: 40px 32px 48px;
    gap: 40px;
  }

  .middle-image-box.expanded .middle-audio-image {
    min-height: 620px;
  }

  .middle-image-box.expanded .controls-column {
    max-width: none;
    width: 100%;
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    align-items: center;
  }

  .middle-image-box.expanded .controls-column .control-card {
    width: 100%;
    max-width: 360px;
    border: none;
    background: transparent;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
  }

  .middle-image-box.expanded .controls-column .control-buttons {
    flex-direction: row;
    justify-content: center;
    width: 100%;
  }

  .middle-image-box.expanded .controls-column .control-button.primary,
  .middle-image-box.expanded .controls-column .fine-print,
  .middle-image-box.expanded .controls-column .native-audio {
    display: none;
  }

  .middle-image-box.expanded .controls-column .control-button {
    min-width: 180px;
  }

  .middle-image-box.expanded .controls-column .control-sub {
    display: none;
  }

  .middle-audio-image {
    flex: 1;
    min-height: 360px;
    border-radius: 16px;
    background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 32%,
        rgba(0, 0, 0, 0.25) 55%,
        rgba(0, 0, 0, 0.55) 75%,
        rgba(0, 0, 0, 0.92) 100%
      ),
      linear-gradient(0deg, rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.35)),
      url("/images/audio-live-bg.png");
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    padding: 32px;
    position: relative;
    color: #ffffff;
    overflow: hidden;
  }

  .image-overlay-content {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: 32px;
    height: 100%;
  }

  .listen-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .status-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 16px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    background: rgba(59, 130, 246, 0.32);
    color: #e4edff;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    text-shadow: 0 6px 20px rgba(0, 0, 0, 0.6);
  }

  .status-pill.live {
    background: rgba(237, 28, 36, 0.32);
    color: #ffd6d6;
  }

  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
  }

  .stream-meta {
    font-size: 12px;
    color: #f1f1f1;
    background: rgba(23, 23, 23, 0.65);
    padding: 6px 12px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    text-shadow: 0 6px 20px rgba(0, 0, 0, 0.6);
  }

  .listen-play {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .listen-play h2 {
    margin: 0;
    font-size: 28px;
    font-weight: 600;
    color: #ffffff;
    text-shadow: 0 8px 28px rgba(0, 0, 0, 0.65);
  }

  .listen-play span.description {
    font-size: 14px;
    color: #f4f4f4;
    max-width: 420px;
    line-height: 1.65;
    text-shadow: 0 6px 22px rgba(0, 0, 0, 0.56);
  }

  .listners {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #ffffff;
    font-size: 13px;
    font-weight: 500;
    text-shadow: 0 6px 22px rgba(0, 0, 0, 0.56);
  }

  .support-text {
    color: rgba(237, 237, 237, 0.8);
    font-size: 12px;
    max-width: 420px;
    line-height: 1.65;
    text-shadow: 0 6px 22px rgba(0, 0, 0, 0.56);
  }

  .play-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  .play-overlay button {
    pointer-events: auto;
    width: 72px;
    height: 72px;
    border-radius: 50%;
    border: none;
    background: rgba(237, 28, 36, 0.92);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 16px 40px rgba(237, 28, 36, 0.45);
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .play-overlay button:hover {
    transform: translateY(-2px);
    box-shadow: 0 20px 50px rgba(237, 28, 36, 0.55);
  }

  .expand-toggle {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 2;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: rgba(17, 17, 17, 0.85);
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.2s ease, background 0.2s ease;
  }

  .expand-toggle:hover {
    transform: translateY(-1px);
    background: rgba(17, 17, 17, 0.95);
  }

  .controls-column {
    width: 100%;
    max-width: 340px;
    border-radius: 16px;
    border: 1px solid #272a31;
    background: #111111;
    padding: 24px;
    display: flex;
    justify-content: space-between;

    gap: 20px;
  }

  .control-card h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #eff0fa;
  }

  .control-sub {
    margin: 6px 0 0;
    font-size: 13px;
    color: #c5c6d0;
  }

  .control-buttons {
    display: flex;
    width: 100%;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 24px 0 12px;
  }

  .device-selects {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 8px;
  }

  .device-selects .device-select {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .device-selects .device-select label {
    font-size: 13px;
    font-weight: 500;
    color: #c5c6d0;
  }

  .device-selects .device-select select {
    border-radius: 10px;
    border: 1px solid #2f3035;
    background: #1b1b1d;
    color: #eff0fa;
    padding: 10px 12px;
    font-size: 14px;
  }

  .device-selects .device-select select:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .device-controls-row {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
  }

  .device-controls-row .device-refresh {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: 1px solid #272a31;
    border-radius: 10px;
    background: #1b1b1b;
    color: #eff0fa;
    padding: 8px 12px;
    font-size: 13px;
    cursor: pointer;
  }

  .device-controls-row .device-refresh:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .device-selects .device-help {
    font-size: 12px;
    color: #8d8f99;
    margin: 0;
  }

  .device-error {
    color: #fca5a5;
    font-size: 12px;
    margin: 6px 0 0;
  }

  .call-controls {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  .call-controls .control-row {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .call-controls .control-row label {
    font-size: 13px;
    font-weight: 500;
    color: #c5c6d0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .call-controls .control-row input[type="range"] {
    width: 100%;
  }

  .call-controls .control-row .control-button.small {
    align-self: flex-start;
  }

  .control-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    border: 1px solid #272a31;
    border-radius: 12px;
    padding: 12px 18px;
    font-size: 14px;
    font-weight: 600;
    background: #1b1b1b;
    color: #eff0fa;
    cursor: pointer;
    transition: transform 0.15s ease, background 0.2s ease,
      border-color 0.2s ease;
  }

  .control-button.primary {
    background: #ed1c24;
    border-color: #ed1c24;
    color: #ffffff;
  }

  .control-button:hover:not(:disabled) {
    transform: translateY(-1px);
    background: #232323;
  }

  .control-button.primary:hover:not(:disabled) {
    background: #ff2c34;
    border-color: #ff2c34;
  }

  .control-button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
  }

  .native-audio {
    width: 300px;
    border-radius: 12px;
    background: #000000;
    border: 1px solid #272a31;
    padding: 8px 12px;
  }

  .fine-print {
    margin: 0;
    font-size: 12px;
    color: #8d8f99;
    line-height: 1.6;
  }

  @media (max-width: 1024px) {
    .middle-image-box {
      flex-direction: column;
    }

    .controls-column {
      max-width: none;
      width: 100%;
    }
  }

  @media (max-width: 640px) {
    .middle-image-box {
      padding: 24px;
      gap: 24px;
    }

    .middle-audio-image {
      padding: 24px;
    }
    .middle-image-box {
      padding: 2px !important;
      background: transparent !important;
    }
    .middle-image-box.expanded .middle-audio-image {
      min-height: 400px !important;
      box-shadow: 0 0 12px 4px rgba(255, 255, 255, 0.55),
        0 0 25px 10px rgba(0, 150, 255, 0.45);
    }

    .upper-audio {
      flex-direction: column;
      align-items: stretch;
    }

    .identity-row {
      width: 100%;
      justify-content: space-between;
    }

    .title-block {
      margin-top: 12px;
    }

    .status-group {
      width: 100%;
      justify-content: center;
    }

    .control-buttons {
      justify-content: center;
      gap: 10px;
    }

    .control-button {
      width: 54px;
      height: 54px;
      padding: 0;
      border-radius: 50%;
    }

    .control-button .control-label {
      display: none;
    }

    .listen-play h2 {
      font-size: 22px;
    }

    .play-overlay button {
      width: 60px;
      height: 60px;
    }
  }
`;

const CallPromptBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 1000;
`;

const CallPromptCard = styled.form`
  width: 100%;
  max-width: 360px;
  background: #111217;
  border-radius: 16px;
  border: 1px solid #272a31;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #eff0fa;
  }

  p {
    margin: 0;
    color: #c5c6d0;
    font-size: 13px;
    line-height: 1.5;
  }

  input {
    width: 100%;
    padding: 10px 12px;
    border-radius: 10px;
    border: 1px solid #2f3035;
    background: #1b1b1d;
    color: #eff0fa;
    font-size: 14px;
  }

  input:focus {
    outline: none;
    border-color: #ff6b72;
    box-shadow: 0 0 0 2px rgba(255, 107, 114, 0.2);
  }

  input:disabled {
    opacity: 0.65;
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }

  .actions button {
    border: none;
    border-radius: 999px;
    padding: 8px 18px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.15s ease;
  }

  .actions button:first-child {
    background: rgba(255, 255, 255, 0.08);
    color: #eff0fa;
  }

  .actions button:last-child {
    background: #ff6b72;
    color: #111217;
  }

  .actions button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .actions button:not(:disabled):hover {
    transform: translateY(-1px);
  }
`;

const ControlsModalBackdrop = styled(CallPromptBackdrop)`
  backdrop-filter: blur(2px);
`;

const ControlsModalCard = styled.div`
  width: 100%;
  max-width: 420px;
  background: #101116;
  border-radius: 18px;
  border: 1px solid #2b2d35;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.5);

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .modal-header h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #f2f4ff;
  }

  .modal-header button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 12px;
    border: 1px solid #2f313a;
    background: rgba(255, 255, 255, 0.04);
    color: #f2f4ff;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.15s ease;
  }

  .modal-header button:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-1px);
  }

  .modal-body {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }
`;

const ControlPanel = styled.div`
  background: #111111;
  border: 1px solid #272a31;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ControlHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  position: sticky;
  top: -20px;
  background: #111111;
  z-index: 10;
  padding: 12px 0 16px;
  border-bottom: 1px solid #272a31;
  margin-bottom: 16px;
  width: 100%;

  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #eff0fa;
  }
`;

const StatusChip = styled.span`
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: ${({ $variant }) =>
    $variant === "success"
      ? "rgba(34, 197, 94, 0.15)"
      : "rgba(237, 28, 36, 0.15)"};
  color: ${({ $variant }) => ($variant === "success" ? "#4ade80" : "#ff4d4d")};
  border: 1px solid
    ${({ $variant }) =>
      $variant === "success"
        ? "rgba(34, 197, 94, 0.3)"
        : "rgba(237, 28, 36, 0.3)"};
`;

const PanelTabs = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 999px;
  background: #1c1c1c;
  margin-bottom: 20px;
`;

const PanelActionTab = styled.button`
  border: none;
  border-radius: 999px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: ${({ $active }) => ($active ? "#ed1c24" : "transparent")};
  color: ${({ $active }) => ($active ? "#ffffff" : "#c5c6d0")};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ $active }) => ($active ? "#ed1c24" : "#2d2d2d")};
    color: #ffffff;
  }
`;

const ChatContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.02);
  min-height: 400px;
  max-height: 600px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #333;
    border-radius: 10px;
  }
`;

const ChatMessage = styled.div`
  display: flex;
  gap: 12px;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    overflow: hidden;
    background: #222;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .sender {
    font-size: 13px;
    font-weight: 600;
    color: #e4edff;
    display: flex;
    align-items: center;
    gap: 8px;

    .time {
      font-size: 11px;
      font-weight: 400;
      color: #8f9099;
    }
  }

  .text {
    font-size: 14px;
    line-height: 1.5;
    color: #eff0fa;
    word-break: break-word;
  }
`;

const ReactionWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
`;

const ReactionButton = styled.button`
  background: ${({ $active }) =>
    $active ? "rgba(237, 28, 36, 0.15)" : "#222"};
  border: 1px solid ${({ $active }) => ($active ? "#ed1c24" : "#333")};
  color: ${({ $active }) => ($active ? "#ed1c24" : "#8f9099")};
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #ed1c24;
    color: #ed1c24;
  }

  span {
    font-weight: 600;
  }
`;

const EmptyState = styled.p`
  font-size: 14px;
  color: #8f9099;
  text-align: center;
  margin: 20px 0;
`;

const LoginPromptWrapper = styled.div`
  margin-top: 16px;
  padding: 24px;
  background: #1b1b1b;
  border-radius: 12px;
  border: 1px solid #272a31;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;

  p {
    margin: 0;
    font-size: 14px;
    color: #c5c6d0;
  }
`;

const LoginButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: #ed1c24;
  color: white;
  padding: 12px 24px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: #ff2c34;
    transform: translateY(-1px);
    box-shadow: 0 8px 20px rgba(237, 28, 36, 0.25);
  }
`;

function getOptsValue() {
  try {
    const urlObject = new URL(window.location.href);
    return urlObject.searchParams.get("opts");
  } catch (err) {
    console.error("Invalid URL", err);
    return null;
  }
}

function fromUrlSafeBase64(urlSafeBase64String) {
  if (!urlSafeBase64String) return null;
  let standardBase64 = urlSafeBase64String
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  while (standardBase64.length % 4 !== 0) {
    standardBase64 += "=";
  }
  return standardBase64;
}

function base64Decode(base64String) {
  try {
    const binaryString = atob(base64String);
    const utf8Bytes = Uint8Array.from(binaryString, (char) =>
      char.charCodeAt(0)
    );
    return new TextDecoder().decode(utf8Bytes);
  } catch (err) {
    console.error("Base64 decode failed", err);
    return null;
  }
}

function urlSafeBase64ToText(urlSafeBase64) {
  if (!urlSafeBase64) return null;
  const standardBase64 = fromUrlSafeBase64(urlSafeBase64);
  if (!standardBase64) return null;
  return base64Decode(standardBase64);
}

function decodeStreamOpts(raw) {
  if (!raw) return null;
  const json = urlSafeBase64ToText(raw);
  if (!json) return null;
  try {
    return JSON.parse(json);
  } catch (err) {
    console.error("Invalid stream payload", err);
    return null;
  }
}

const defaultListener = {
  id: "anon-listener",
  email: undefined,
};

const NonUserListener = () => {
  const audioRef = useRef(null);
  const monitorAudioRef = useRef(null);
  const listenerRef = useRef(null);
  const socketRef = useRef(null);
  const mountedRef = useRef(false);
  const retryAttemptsRef = useRef(0);
  const retryTimeoutRef = useRef(null);
  const retryScheduledRef = useRef(false);
  const startListenerRef = useRef(null);
  const scheduleRetryRef = useRef(null);

  const dispatch = useDispatch();
  const { eventImages, specificEvent } = useSelector((state) => state.userData);

  const [status, setStatus] = useState("Preparing stream");
  const [error, setError] = useState(null);
  const [callError, setCallError] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [stationName, setStationName] = useState("Pluglr Radio");
  const [streamTitle, setStreamTitle] = useState("Listener stream");
  const [streamDescription, setStreamDescription] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCallPrompt, setShowCallPrompt] = useState(false);
  const [callerName, setCallerName] = useState("");
  const [callState, setCallState] = useState({ status: "idle", message: "" });
  const [connectionDetails, setConnectionDetails] = useState({
    channelId: null,
    eventId: null,
    roomToken: null,
  });
  const [audienceLevel, setAudienceLevel] = useState(100);
  const [audienceMuted, setAudienceMuted] = useState(false);
  const [selfMonitorEnabled, setSelfMonitorEnabled] = useState(false);
  const [selfMonitorLevel, setSelfMonitorLevel] = useState(50);
  const [callControlsOpen, setCallControlsOpen] = useState(false);
  const [audioDevices, setAudioDevices] = useState({ inputs: [], outputs: [] });
  const [selectedMicId, setSelectedMicId] = useState("default");
  const [selectedSpeakerId, setSelectedSpeakerId] = useState("default");
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [deviceError, setDeviceError] = useState(null);
  const [speakerError, setSpeakerError] = useState(null);
  const [panelTab, setPanelTab] = useState("audio");
  const chatContainerRef = useRef(null);

  const { comments, commentsLoading, commentsHasMore, commentsPage } =
    useSelector((state) => state.userData);

  const canSelectSpeaker = useMemo(() => {
    if (typeof document === "undefined") return false;
    const audio = document.createElement("audio");
    return typeof audio.setSinkId === "function";
  }, []);

  const fetchAudioDevices = useCallback(async () => {
    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.enumerateDevices
    ) {
      setDeviceError(
        "Audio device selection is not supported in this browser."
      );
      setAudioDevices({ inputs: [], outputs: [] });
      return;
    }

    setDevicesLoading(true);
    setDeviceError(null);
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = [];
      const outputs = [];
      let micCounter = 1;
      let speakerCounter = 1;

      devices.forEach((device) => {
        if (device.kind === "audioinput" && device.deviceId !== "default") {
          inputs.push({
            deviceId: device.deviceId,
            label: device.label || `Microphone ${micCounter++}`,
          });
        }
        if (device.kind === "audiooutput" && device.deviceId !== "default") {
          outputs.push({
            deviceId: device.deviceId,
            label: device.label || `Speaker ${speakerCounter++}`,
          });
        }
      });

      setAudioDevices({ inputs, outputs });

      setSelectedMicId((prev) => {
        if (prev === "default") return prev;
        return inputs.some((device) => device.deviceId === prev)
          ? prev
          : "default";
      });

      setSelectedSpeakerId((prev) => {
        if (prev === "default") return prev;
        return outputs.some((device) => device.deviceId === prev)
          ? prev
          : "default";
      });
    } catch (err) {
      console.warn("enumerateDevices failed", err);
      setDeviceError(err?.message || "Unable to list audio devices.");
    } finally {
      setDevicesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAudioDevices();
    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.addEventListener
    ) {
      return undefined;
    }
    const handleDeviceChange = () => {
      fetchAudioDevices();
    };
    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        handleDeviceChange
      );
    };
  }, [fetchAudioDevices]);

  const linkDetails = useMemo(() => {
    const rawToken = getOptsValue();
    if (!rawToken) {
      console.warn("NonUserListener: opts parameter missing");
      return { rawToken: null, payload: null, station: null, event: null };
    }

    console.log("NonUserListener: raw opts token", rawToken);

    const payload = decodeStreamOpts(rawToken);
    if (!payload) {
      console.warn("NonUserListener: could not decode stream payload");
      return { rawToken, payload: null, station: null, event: null };
    }

    console.log("NonUserListener: decoded stream payload", payload);
    if (payload.jwt) {
      console.log("NonUserListener: extracted JWT token", payload.jwt);
    } else {
      console.warn("NonUserListener: payload missing jwt token");
    }

    const station = {
      id: payload.channelId,
      name: payload.name,
    };

    const event = {
      id: payload.eventId,
      title: payload.title,
      description: payload.description,
    };

    return { rawToken, payload, station, event };
  }, []);

  const linkPayload = linkDetails.payload;
  const channelId = linkPayload?.channelId || null;
  const eventId = linkPayload?.eventId || null;
  const socketToken = linkDetails.rawToken || null;

  const defaultEventImage = "/images/audio-live-bg.png";
  const eventBackgroundImage = eventImages?.[eventId] ?? defaultEventImage;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!channelId || !eventId || !socketToken) {
      setError("Missing stream details. Please confirm the link.");
      setStatus("Invalid link");
      return;
    }

    setConnectionDetails({ channelId, eventId, roomToken: socketToken });

    if (linkDetails.station?.name) {
      setStationName(linkDetails.station.name);
    }
    if (linkDetails.event?.title) {
      setStreamTitle(linkDetails.event.title);
    }
    if (linkDetails.event?.description) {
      setStreamDescription(linkDetails.event.description);
    } else if (linkDetails.station?.name) {
      setStreamDescription(`Streaming from ${linkDetails.station.name}.`);
    } else {
      setStreamDescription("Live broadcast hosted on Pluglr.");
    }
  }, [channelId, eventId, socketToken, linkDetails]);

  useEffect(() => {
    if (!eventId) return;
    dispatch(fetchSpecificEvent(eventId));
    if (!eventImages?.[eventId]) {
      dispatch(fetchEventImage(eventId));
    }
  }, [dispatch, eventId, eventImages]);

  useEffect(() => {
    const eventData = specificEvent?.data;
    if (!eventData) return;
    if (eventId && eventData.id && `${eventData.id}` !== `${eventId}`) return;

    if (eventData.title) {
      setStreamTitle(eventData.title);
    }
    if (eventData.description) {
      setStreamDescription(eventData.description);
    }
    if (eventData.channel?.name) {
      setStationName(eventData.channel.name);
    }
  }, [specificEvent, eventId]);

  // Fetch comments when chat tab is active
  useEffect(() => {
    if (panelTab === "chat" && eventId) {
      if ((comments?.content?.length || 0) === 0) {
        dispatch(fetchComments({ eventId, page: 0 }));
      }
    }
  }, [panelTab, eventId, dispatch, comments?.content?.length]);

  const handleChatScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      !commentsLoading &&
      commentsHasMore
    ) {
      dispatch(fetchComments({ eventId, page: commentsPage + 1 }));
    }
  };

  const handleReaction = async (commentId, reaction, currentReaction) => {
    try {
      if (reaction === "NOT_SET" || reaction === currentReaction) {
        await dispatch(removeReaction(commentId));
        if (socketRef.current) {
          socketRef.current.emit(
            "reactionRemove",
            { commentId, reactionType: currentReaction, userId: "guest" },
            (err) => {
              if (err) {
                console.error(
                  "error while broadcasting reactionRemove:",
                  JSON.stringify(err)
                );
              } else {
                console.log("comment broadcasted (reaction removed)");
              }
            }
          );
        }
      } else {
        await dispatch(addReaction({ commentId, reaction }));
        if (socketRef.current) {
          socketRef.current.emit(
            "reactionAdd",
            {
              commentId,
              reactionType: reaction,
              previousReactionType: currentReaction || null,
              userId: "guest",
            },
            (err) => {
              if (err) {
                console.error(
                  "error while broadcasting reactionAdd:",
                  JSON.stringify(err)
                );
              } else {
                console.log("comment broadcasted (reaction added)");
              }
            }
          );
        }
      }
    } catch (err) {
      console.error("Failed to handle reaction:", err);
    }
  };

  const timeAgo = (timestamp) => {
    if (!timestamp) return "";
    const seconds = Math.floor((new Date() - new Date(timestamp)) / 1000);
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + "y ago";
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + "m ago";
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + "d ago";
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + "h ago";
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + "m ago";
    return Math.floor(seconds) + "s ago";
  };

  const attemptMonitorPlayback = useCallback(() => {
    const monitor = monitorAudioRef.current;
    if (!monitor || !monitor.srcObject) return;
    if (!monitor.paused) return;
    monitor.play().catch((err) => {
      console.warn("Monitor playback attempt failed", err);
    });
  }, []);

  const attemptPlayback = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return Promise.resolve(false);

    return audio
      .play()
      .then(() => {
        if (!mountedRef.current) return true;
        setIsPlaying(true);
        setIsMuted(audio.muted);
        setAutoplayBlocked(false);
        return true;
      })
      .catch((err) => {
        console.warn("Audio playback attempt failed", err);
        if (!mountedRef.current) return false;
        setIsReady(true);
        setIsPlaying(false);
        setIsMuted(audio.muted);
        setAutoplayBlocked(true);
        return false;
      });
  }, []);

  useEffect(() => {
    const {
      channelId: resolvedChannelId,
      eventId: resolvedEventId,
      roomToken,
    } = connectionDetails || {};
    if (!resolvedChannelId || !resolvedEventId || !roomToken) {
      return undefined;
    }

    retryAttemptsRef.current = 0;
    retryScheduledRef.current = false;
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    const clearRetryTimer = () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };

    const cleanupConnections = () => {
      const activeListener = listenerRef.current;
      if (activeListener) {
        listenerRef.current = null;
        activeListener
          .cleanup()
          .catch((err) => console.warn("cleanup error", err));
      }

      const activeSocket = socketRef.current;
      if (activeSocket) {
        activeSocket.removeAllListeners();
        activeSocket.disconnect();
        socketRef.current = null;
      }

      if (audioRef.current) {
        try {
          audioRef.current.srcObject = null;
        } catch (err) {
          console.warn("Audio cleanup", err);
        }
      }
    };

    const scheduleRetry = (reason) => {
      if (!mountedRef.current) return;

      cleanupConnections();
      clearRetryTimer();

      if (retryScheduledRef.current) {
        setError(reason || "Reconnecting…");
        return;
      }

      const nextAttempt = retryAttemptsRef.current + 1;
      if (nextAttempt > MAX_CONNECTION_RETRIES) {
        retryAttemptsRef.current = nextAttempt;
        retryScheduledRef.current = false;
        const finalMessage =
          reason ||
          "Unable to connect to the stream. Please check your connection and try again.";
        setError(finalMessage);
        setStatus("Disconnected");
        window.alert(finalMessage);
        return;
      }

      retryAttemptsRef.current = nextAttempt;
      retryScheduledRef.current = true;

      const reconnectMessage = reason || "Attempting to reconnect…";
      setError(reconnectMessage);
      setStatus(
        `Reconnecting… (attempt ${nextAttempt} of ${MAX_CONNECTION_RETRIES})`
      );

      retryTimeoutRef.current = window.setTimeout(() => {
        retryTimeoutRef.current = null;
        retryScheduledRef.current = false;
        if (!mountedRef.current) return;
        startListenerRef.current?.();
      }, RETRY_DELAY_MS);
    };

    scheduleRetryRef.current = scheduleRetry;

    const handleSocketDisconnect = (reason) => {
      if (!mountedRef.current) return;
      const detail =
        typeof reason === "string" && reason.length > 0
          ? `Connection lost: ${reason}`
          : "Connection lost.";
      scheduleRetry(detail);
    };

    const handleSocketError = (err) => {
      if (!mountedRef.current) return;
      const message = err?.message || "Socket connection failed";
      scheduleRetry(message);
    };

    const onSpeaking = (speaking) => {
      if (!mountedRef.current) return;
      const audio = audioRef.current;
      if (audio) {
        // Duck volume to 20% when speaking, restore to 100% when silent
        const targetVolume = speaking ? 0.2 : 1.0;
        audio.volume = targetVolume;
      }
    };

    const startListener = async () => {
      if (!mountedRef.current) return;

      clearRetryTimer();
      retryScheduledRef.current = false;

      cleanupConnections();

      setStatus("Connecting to stream");
      setError(null);

      try {
        const socket = io(SIGNALING_URL, {
          path: "/socket.io",
          transports: ["websocket"],
          query: {
            token: roomToken,
            channelId: resolvedChannelId,
            eventId: resolvedEventId,
            role: "listener",
          },
        });

        socket.once("connect_error", handleSocketError);
        socket.on("disconnect", handleSocketDisconnect);

        // Real-time Chat & Reactions
        socket.on("onComment", (comment) => {
          console.log("Received real-time comment:", comment);
          dispatch(receiveComment(comment));
        });

        socket.on("onReactionAdd", (data) => {
          console.log("Received real-time reactionAdd:", data);
          dispatch(receiveReactionAdd(data));
        });

        socket.on("onReactionRemove", (data) => {
          console.log("Received real-time reactionRemove:", data);
          dispatch(receiveReactionRemove(data));
        });

        socketRef.current = socket;

        const listener = new WebRTCListenerClient({
          socket,
          channelId: resolvedChannelId,
          eventId: resolvedEventId,
          user: { name: "Guest Listener", displayName: "Guest Listener" },
          onStatusChange: (message) => {
            if (!mountedRef.current) return;
            setStatus(message);
          },
          onError: (err) => {
            if (!mountedRef.current) return;
            const message = err?.message || "Listener error";
            console.error("Listener error", err);
            setError(message);
            setStatus("Encountered an error");
            scheduleRetry(message);
          },
          onTrack: (stream) => {
            if (!mountedRef.current) return;
            if (!audioRef.current) return;

            audioRef.current.srcObject = stream;
            setIsReady(true);
            attemptPlayback();
          },
          onCallStateChange: (state) => {
            if (!mountedRef.current) return;
            setCallState(state || { status: "idle", message: "" });
          },
          onSpeaking,
        });

        listenerRef.current = listener;

        await listener.start();

        if (!mountedRef.current) {
          return;
        }

        retryAttemptsRef.current = 0;
        retryScheduledRef.current = false;
        setStatus("Listening");
        setError(null);
        setCallError(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        console.error("Failed to start listener", err);
        if (!mountedRef.current) return;
        scheduleRetry(message);
      }
    };

    startListenerRef.current = startListener;

    startListener();

    return () => {
      clearRetryTimer();
      retryScheduledRef.current = false;
      retryAttemptsRef.current = 0;
      scheduleRetryRef.current = null;
      startListenerRef.current = null;
      cleanupConnections();
    };
  }, [connectionDetails, attemptPlayback]);

  const handleOpenCallPrompt = useCallback(() => {
    setCallerName("");
    setCallError(null);
    setShowCallPrompt(true);
  }, []);

  const handleCloseCallPrompt = useCallback(() => {
    if (callState.status === "requesting") return;
    setShowCallPrompt(false);
  }, [callState.status]);

  const handleCallNameChange = useCallback((event) => {
    setCallerName(event.target.value);
  }, []);

  const handleSubmitCallRequest = useCallback(
    async (event) => {
      event?.preventDefault();
      if (!listenerRef.current) {
        setCallError("Connection not ready yet");
        return;
      }

      const trimmed = callerName.trim();
      if (!trimmed) {
        setCallError("Please enter your name");
        return;
      }

      setCallError(null);
      try {
        await listenerRef.current.requestCall(trimmed);
        setShowCallPrompt(false);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setCallError(message);
      }
    },
    [callerName]
  );

  const handleTogglePlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      attemptPlayback();
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const handleMuteToggle = () => {
    if (!audioRef.current) return;
    const nextMuted = !audioRef.current.muted;
    audioRef.current.muted = nextMuted;
    setIsMuted(nextMuted);
  };

  const handleAudienceVolumeChange = useCallback((event) => {
    const value = Number(event.target.value);
    if (!Number.isFinite(value)) return;
    setAudienceLevel(Math.max(0, Math.min(100, value)));
  }, []);

  const handleAudienceMuteToggle = useCallback(() => {
    setAudienceMuted((prev) => !prev);
  }, []);

  const handleSelfMonitorToggle = useCallback(() => {
    setSelfMonitorEnabled((prev) => !prev);
  }, []);

  const handleSelfMonitorVolumeChange = useCallback((event) => {
    const value = Number(event.target.value);
    if (!Number.isFinite(value)) return;
    setSelfMonitorLevel(Math.max(0, Math.min(100, value)));
  }, []);

  const handleMicSelect = useCallback((event) => {
    setSelectedMicId(event.target.value);
  }, []);

  const handleSpeakerSelect = useCallback((event) => {
    setSelectedSpeakerId(event.target.value);
  }, []);

  const handleRefreshDevices = useCallback(() => {
    fetchAudioDevices();
  }, [fetchAudioDevices]);

  const handleEndCall = useCallback(() => {
    try {
      listenerRef.current?.endCall();
    } catch (err) {
      console.warn("endCall failed", err);
    }
  }, []);

  const middleAudioBackground = useMemo(
    () =>
      `linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 32%,
        rgba(0, 0, 0, 0.25) 55%,
        rgba(0, 0, 0, 0.55) 75%,
        rgba(0, 0, 0, 0.92) 100%
      ),
      linear-gradient(0deg, rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.35)),
      url('${eventBackgroundImage}')`,
    [eventBackgroundImage]
  );

  const callStatusMessage = callState.message || "";
  const isRequestingCall = callState.status === "requesting";
  const isCallLive = useMemo(
    () => ["connected", "producing"].includes(callState.status),
    [callState.status]
  );
  const showCallControls = useMemo(
    () =>
      ["promoted", "capturing", "connected", "producing"].includes(
        callState.status
      ),
    [callState.status]
  );

  useEffect(() => {
    if (!showCallControls) {
      setCallControlsOpen(false);
    }
  }, [showCallControls]);

  const callButtonLabel = useMemo(() => {
    if (isCallLive) return "End call";
    if (isRequestingCall) return "Requesting…";
    return "Call the host";
  }, [isCallLive, isRequestingCall]);

  const callButtonIcon = isCallLive ? "mdi:phone-hangup" : "mdi:phone";

  const handleCallAction = useCallback(() => {
    if (isCallLive) {
      handleEndCall();
      return;
    }
    handleOpenCallPrompt();
  }, [handleEndCall, handleOpenCallPrompt, isCallLive]);

  const callButtonDisabled = isCallLive ? false : isRequestingCall || !isReady;

  const handleOpenCallControls = useCallback(() => {
    if (!showCallControls) return;
    setCallControlsOpen(true);
  }, [showCallControls]);

  const handleCloseCallControls = useCallback(() => {
    setCallControlsOpen(false);
  }, []);

  useEffect(() => {
    if (!callControlsOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleCloseCallControls();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [callControlsOpen, handleCloseCallControls]);

  const statusVariant = error ? "error" : isPlaying ? "success" : "info";

  const liveLabel = statusVariant === "success" ? "Live" : "Connecting";
  const pillLabel =
    statusVariant === "success" ? "Live broadcast" : "Preparing stream";
  const expandToggleLabel = isExpanded ? "Collapse preview" : "Expand preview";
  const expandToggleIcon = isExpanded
    ? "mdi:fullscreen-exit"
    : "mdi:fullscreen";
  const playButtonLabel = isPlaying
    ? "Pause stream"
    : isReady
    ? "Play stream"
    : "Connecting…";
  const canControlPlayback = isReady || isPlaying;

  useEffect(() => {
    if (!autoplayBlocked) return undefined;

    const handleUserGesture = () => {
      attemptPlayback().then((played) => {
        if (played) {
          window.removeEventListener("pointerdown", handleUserGesture);
          window.removeEventListener("keydown", handleUserGesture);
        }
      });
    };

    window.addEventListener("pointerdown", handleUserGesture, { once: true });
    window.addEventListener("keydown", handleUserGesture, { once: true });

    return () => {
      window.removeEventListener("pointerdown", handleUserGesture);
      window.removeEventListener("keydown", handleUserGesture);
    };
  }, [autoplayBlocked, attemptPlayback]);

  useEffect(() => {
    const listener = listenerRef.current;
    if (!listener) return;
    const normalized = audienceLevel / 100;
    listener.setAudienceVolume(normalized);
  }, [audienceLevel]);

  useEffect(() => {
    const listener = listenerRef.current;
    if (!listener) return;
    listener.setAudienceMuted(audienceMuted);
  }, [audienceMuted]);

  useEffect(() => {
    const listener = listenerRef.current;
    if (!listener) return;
    const normalized = selfMonitorLevel / 100;
    listener.setSelfMonitorVolume(normalized);
    if (monitorAudioRef.current) {
      monitorAudioRef.current.volume = normalized;
    }
  }, [selfMonitorLevel]);

  useEffect(() => {
    const listener = listenerRef.current;
    if (!listener) return;
    listener.setSelfMonitorEnabled(selfMonitorEnabled);
    const monitor = monitorAudioRef.current;
    if (!monitor) return;
    monitor.muted = !selfMonitorEnabled;
    if (selfMonitorEnabled) {
      attemptMonitorPlayback();
    } else {
      try {
        monitor.pause();
      } catch (err) {
        console.warn("Pause monitor playback", err);
      }
    }
  }, [selfMonitorEnabled, attemptMonitorPlayback]);

  useEffect(() => {
    const listener = listenerRef.current;
    const monitor = monitorAudioRef.current;
    if (!monitor) return;
    const stream = listener?.getMonitorStream?.();
    if (stream) {
      if (monitor.srcObject !== stream) {
        monitor.srcObject = stream;
      }
      if (selfMonitorEnabled) {
        attemptMonitorPlayback();
      }
    } else if (monitor.srcObject) {
      monitor.srcObject = null;
    }
  }, [callState.status, selfMonitorEnabled, attemptMonitorPlayback]);

  useEffect(() => {
    if (callState.status === "ended" || callState.status === "idle") {
      setSelfMonitorEnabled(false);
      setAudienceMuted(false);
    }
  }, [callState.status]);

  useEffect(() => {
    const listener = listenerRef.current;
    if (!listener || typeof listener.setAudioInputDevice !== "function") {
      return;
    }
    const normalized = selectedMicId === "default" ? null : selectedMicId;
    listener.setAudioInputDevice(normalized);
  }, [selectedMicId]);

  useEffect(() => {
    if (!canSelectSpeaker) {
      return;
    }
    const targets = [audioRef.current, monitorAudioRef.current].filter(
      (element) => element && typeof element.setSinkId === "function"
    );
    if (!targets.length) {
      return;
    }
    const sinkId =
      selectedSpeakerId === "default" ? "default" : selectedSpeakerId;
    let cancelled = false;
    const applySink = async () => {
      setSpeakerError(null);
      for (const element of targets) {
        try {
          await element.setSinkId(sinkId);
        } catch (err) {
          if (cancelled) {
            return;
          }
          console.warn("setSinkId failed", err);
          setSpeakerError(err?.message || "Unable to switch playback device.");
          break;
        }
      }
    };
    applySink();
    return () => {
      cancelled = true;
    };
  }, [selectedSpeakerId, canSelectSpeaker]);

  const callControlsContent = (
    <div className="call-controls">
      <div className="control-row">
        <label htmlFor="audience-level">Audience send level</label>
        <input
          id="audience-level"
          type="range"
          min="0"
          max="100"
          value={audienceLevel}
          onChange={handleAudienceVolumeChange}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={audienceLevel}
        />
        <button
          type="button"
          className="control-button small"
          onClick={handleAudienceMuteToggle}
        >
          <Icon
            icon={audienceMuted ? "mdi:microphone-off" : "mdi:microphone"}
            width={18}
            height={18}
          />
          {audienceMuted ? "Audience muted" : "Audience live"}
        </button>
      </div>

      <div className="control-row">
        <label htmlFor="monitor-toggle">
          <input
            id="monitor-toggle"
            type="checkbox"
            checked={selfMonitorEnabled}
            onChange={handleSelfMonitorToggle}
          />
          Hear yourself
        </label>
        <input
          type="range"
          min="0"
          max="100"
          value={selfMonitorLevel}
          onChange={handleSelfMonitorVolumeChange}
          disabled={!selfMonitorEnabled}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={selfMonitorLevel}
        />
      </div>

      <div className="control-row">
        <button
          type="button"
          className="control-button danger"
          onClick={handleEndCall}
          disabled={!isCallLive}
        >
          <Icon icon="mdi:phone-hangup" width={18} height={18} />
          End call
        </button>
      </div>
    </div>
  );

  return (
    <NonUserShell>
      <div className="all-the-audios">
        <div className="upper-audio">
          <div className="identity-row">
            <div className="brand-mark">
              <img
                src="/images/icon-7.png"
                alt="Pluglr"
                style={{ width: "100%", height: "100%" }}
              />
            </div>
            <div className="title-block">
              <p>{streamTitle}</p>
            </div>
          </div>
          <div className="status-group">
            <div className="audio-upper-middle">
              <div
                className={`red-rounded ${
                  statusVariant === "success" ? "on" : ""
                }`}
              />
              <p>{liveLabel}</p>
              <span>{isPlaying ? "On air" : "Standby"}</span>
            </div>
          </div>
        </div>

        {error ? <p className="error-text">{error}</p> : null}
        {callError ? <p className="error-text">{callError}</p> : null}
        {callStatusMessage && (
          <p className="call-status">{callStatusMessage}</p>
        )}

        <audio
          ref={audioRef}
          autoPlay
          playsInline
          style={{ display: "none" }}
        />
        <audio
          ref={monitorAudioRef}
          autoPlay
          playsInline
          muted={!selfMonitorEnabled}
          style={{ display: "none" }}
        />

        {showCallPrompt ? (
          <CallPromptBackdrop role="dialog" aria-modal="true">
            <CallPromptCard onSubmit={handleSubmitCallRequest}>
              <h3>Call the host</h3>
              <p>Enter your name so the host knows who’s calling.</p>
              <input
                type="text"
                value={callerName}
                onChange={handleCallNameChange}
                placeholder="Your name"
                maxLength={80}
                disabled={isRequestingCall}
                autoFocus
              />
              <div className="actions">
                <button
                  type="button"
                  onClick={handleCloseCallPrompt}
                  disabled={isRequestingCall}
                >
                  Cancel
                </button>
                <button type="submit" disabled={isRequestingCall}>
                  {isRequestingCall ? "Requesting…" : "Send request"}
                </button>
              </div>
            </CallPromptCard>
          </CallPromptBackdrop>
        ) : null}

        <div className={`middle-image-box with-controls expanded`}>
          <div
            className="middle-audio-image"
            style={{
              backgroundImage: middleAudioBackground,
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
            }}
          >
            <button
              type="button"
              className="expand-toggle"
              onClick={() => setIsExpanded((prev) => !prev)}
              aria-pressed={isExpanded}
              aria-label={expandToggleLabel}
            >
              <Icon icon={expandToggleIcon} width={22} height={22} />
            </button>

            {autoplayBlocked ? (
              <div className="play-overlay">
                <button
                  type="button"
                  onClick={handleTogglePlayback}
                  disabled={!canControlPlayback}
                  aria-label={playButtonLabel}
                >
                  <Icon
                    icon={isPlaying ? "mdi:pause" : "mdi:play"}
                    width={28}
                    height={28}
                  />
                </button>
              </div>
            ) : null}

            <div className="image-overlay-content">
              <div className="listen-header">
                <span
                  className={`status-pill ${
                    statusVariant === "success" ? "live" : ""
                  }`}
                >
                  <span className="status-dot" />
                  {pillLabel}
                </span>
              </div>

              <div className="listen-play">
                <h2>{streamTitle}</h2>
                <span className="description">{streamDescription}</span>
                <div className="listners">
                  <Icon icon="octicon:people-24" width={18} height={18} />
                  <span>
                    {isPlaying ? "You are tuned in" : "Waiting for audio"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ width: "100%", maxWidth: "1120px", marginTop: "24px" }}>
          <PanelTabs>
            <PanelActionTab
              $active={panelTab === "audio"}
              onClick={() => setPanelTab("audio")}
            >
              Audio
            </PanelActionTab>
            <PanelActionTab
              $active={panelTab === "chat"}
              onClick={() => setPanelTab("chat")}
            >
              Chat ({comments?.totalElements || 0})
            </PanelActionTab>
          </PanelTabs>

          {panelTab === "audio" && (
            <div className="control-card">
              <div className="control-buttons">
                <button
                  type="button"
                  className="control-button primary"
                  onClick={handleTogglePlayback}
                  disabled={!canControlPlayback}
                >
                  <Icon
                    icon={isPlaying ? "mdi:pause" : "mdi:play"}
                    width={20}
                    height={20}
                  />
                  <span className="control-label">{playButtonLabel}</span>
                </button>

                <button
                  type="button"
                  className="control-button"
                  onClick={handleMuteToggle}
                  disabled={!isReady}
                >
                  <Icon
                    icon={isMuted ? "mdi:volume-off" : "mdi:volume-high"}
                    width={20}
                    height={20}
                  />
                  <span className="control-label">
                    {isMuted ? "Unmute" : "Mute"}
                  </span>
                </button>

                <button
                  type="button"
                  className="control-button tertiary"
                  onClick={handleCallAction}
                  disabled={callButtonDisabled}
                >
                  <Icon icon={callButtonIcon} width={18} height={18} />
                  <span className="control-label">{callButtonLabel}</span>
                </button>

                {showCallControls ? (
                  <button
                    type="button"
                    className={`control-button secondary ${
                      callControlsOpen ? "active" : ""
                    }`}
                    onClick={handleOpenCallControls}
                  >
                    <Icon icon="mdi:tune" width={18} height={18} />
                    <span className="control-label">Control</span>
                  </button>
                ) : null}
              </div>

              <div className="device-selects">
                <div className="device-select">
                  <label htmlFor="listener-mic-select">
                    Microphone for calls
                  </label>
                  <select
                    id="listener-mic-select"
                    value={selectedMicId}
                    onChange={handleMicSelect}
                    disabled={
                      devicesLoading && audioDevices.inputs.length === 0
                    }
                  >
                    <option value="default">System default microphone</option>
                    {audioDevices.inputs.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="device-select">
                  <label htmlFor="listener-speaker-select">
                    Speaker for playback
                  </label>
                  <select
                    id="listener-speaker-select"
                    value={selectedSpeakerId}
                    onChange={handleSpeakerSelect}
                    disabled={
                      !canSelectSpeaker ||
                      (devicesLoading && audioDevices.outputs.length === 0)
                    }
                  >
                    <option value="default">System default speaker</option>
                    {audioDevices.outputs.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label}
                      </option>
                    ))}
                  </select>
                  {!canSelectSpeaker ? (
                    <p className="device-help">
                      Your browser does not support selecting a specific
                      speaker.
                    </p>
                  ) : null}
                </div>

                <div className="device-controls-row">
                  <button
                    type="button"
                    className="device-refresh"
                    onClick={handleRefreshDevices}
                    disabled={devicesLoading}
                  >
                    <Icon icon="mdi:refresh" width={16} height={16} />
                    <span>
                      {devicesLoading
                        ? "Refreshing devices…"
                        : "Refresh devices"}
                    </span>
                  </button>
                </div>

                {deviceError ? (
                  <p className="device-error">{deviceError}</p>
                ) : null}
                {speakerError ? (
                  <p className="device-error">{speakerError}</p>
                ) : null}
              </div>
            </div>
          )}

          {panelTab === "chat" && (
            <ControlPanel>
              <ChatContainer ref={chatContainerRef} onScroll={handleChatScroll}>
                <ControlHeader>
                  <h2>Live Chat</h2>
                  <StatusChip $variant="success">Active</StatusChip>
                </ControlHeader>

                {comments?.content?.map((comment, idx) => (
                  <ChatMessage key={comment.id || idx}>
                    <div className="avatar">
                      <img
                        src={
                          comment.user?.image ||
                          comment.user?.profileImage ||
                          "/images/image_1.png"
                        }
                        alt="User"
                      />
                    </div>
                    <div className="content">
                      <div className="sender">
                        {comment.username}
                        <span className="time">
                          {timeAgo(comment.timestamp)}
                        </span>
                      </div>
                      <div className="text">{comment.text}</div>
                      <ReactionWrapper>
                        <ReactionButton
                          $active={comment.userReaction === "LIKE"}
                          onClick={() =>
                            handleReaction(
                              comment.id || comment._id,
                              "LIKE",
                              comment.userReaction
                            )
                          }
                          title="Like"
                        >
                          <Icon icon="mdi:thumb-up" width={14} />
                          {comment.reactionCounts?.LIKE > 0 && (
                            <span>{comment.reactionCounts.LIKE}</span>
                          )}
                        </ReactionButton>
                        <ReactionButton
                          $active={comment.userReaction === "DISLIKE"}
                          onClick={() =>
                            handleReaction(
                              comment.id || comment._id,
                              "DISLIKE",
                              comment.userReaction
                            )
                          }
                          title="Dislike"
                        >
                          <Icon icon="mdi:thumb-down" width={14} />
                          {comment.reactionCounts?.DISLIKE > 0 && (
                            <span>{comment.reactionCounts.DISLIKE}</span>
                          )}
                        </ReactionButton>
                        <ReactionButton
                          $active={comment.userReaction === "LOVE"}
                          onClick={() =>
                            handleReaction(
                              comment.id || comment._id,
                              "LOVE",
                              comment.userReaction
                            )
                          }
                          title="Love"
                        >
                          <Icon icon="mdi:heart" width={14} />
                          {comment.reactionCounts?.LOVE > 0 && (
                            <span>{comment.reactionCounts.LOVE}</span>
                          )}
                        </ReactionButton>
                        <ReactionButton
                          $active={comment.userReaction === "INDIFFERENT"}
                          onClick={() =>
                            handleReaction(
                              comment.id || comment._id,
                              "INDIFFERENT",
                              comment.userReaction
                            )
                          }
                          title="Indifferent"
                        >
                          <Icon icon="mdi:emoticon-neutral" width={14} />
                          {comment.reactionCounts?.INDIFFERENT > 0 && (
                            <span>{comment.reactionCounts.INDIFFERENT}</span>
                          )}
                        </ReactionButton>
                        <ReactionButton
                          onClick={() =>
                            handleReaction(
                              comment.id || comment._id,
                              "NOT_SET",
                              comment.userReaction
                            )
                          }
                          title="Clear"
                        >
                          <Icon icon="mdi:close-circle" width={14} />
                        </ReactionButton>
                      </ReactionWrapper>
                    </div>
                  </ChatMessage>
                ))}

                {commentsLoading && (
                  <div style={{ textAlign: "center", padding: "10px" }}>
                    <ClipLoader size={20} color="#ed1c24" />
                  </div>
                )}

                {!commentsHasMore && (comments?.content?.length || 0) > 0 && (
                  <EmptyState>End of comments</EmptyState>
                )}

                {!commentsLoading && (comments?.content?.length || 0) === 0 && (
                  <EmptyState>No comments yet.</EmptyState>
                )}
              </ChatContainer>

              <LoginPromptWrapper>
                <p>Sign in to join the conversation and post comments.</p>
                <LoginButton href="/login">
                  <Icon icon="mdi:login" width={20} height={20} />
                  Connect to Send Comment
                </LoginButton>
              </LoginPromptWrapper>
            </ControlPanel>
          )}
        </div>
      </div>

      {showCallControls && callControlsOpen ? (
        <ControlsModalBackdrop
          role="dialog"
          aria-modal="true"
          onClick={handleCloseCallControls}
        >
          <ControlsModalCard onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>Live call controls</h3>
              <button
                type="button"
                onClick={handleCloseCallControls}
                aria-label="Close call controls"
              >
                <Icon icon="mdi:close" width={20} height={20} />
              </button>
            </div>
            <div className="modal-body">{callControlsContent}</div>
          </ControlsModalCard>
        </ControlsModalBackdrop>
      ) : null}
    </NonUserShell>
  );
};

export default NonUserListener;
